import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DIVISIONS, LEAD_STATUSES, ADMISSION_MODES, divisionLabel } from "@/lib/roles";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AiAssistPanel from "@/components/AiAssistPanel";
import { Trash2, Phone, Mail, MessageCircle, StickyNote, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_COLOR = {
  new:       { bg: "#E0F2FE", fg: "#0284C7" },
  contacted: { bg: "#DBEAFE", fg: "#1D4ED8" },
  qualified: { bg: "#EDE9FE", fg: "#6D28D9" },
  proposal:  { bg: "#FEF3C7", fg: "#B45309" },
  won:       { bg: "#DCFCE7", fg: "#15803D" },
  lost:      { bg: "#FEE2E2", fg: "#B91C1C" },
};

const COMM_ICONS = { call: Phone, email: Mail, whatsapp: MessageCircle, note: StickyNote, meeting: Send };

export default function LeadDrawer({ leadId, open, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [comms, setComms] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [commForm, setCommForm] = useState({ type: "note", body: "", subject: "" });

  const load = async () => {
    if (!leadId) return;
    const [l, c, u] = await Promise.all([
      api.get(`/leads/${leadId}`),
      api.get(`/communications`, { params: { related_type: "lead", related_id: leadId }}),
      api.get(`/users`),
    ]);
    setLead(l.data);
    setComms(c.data);
    setUsers(u.data);
  };

  useEffect(() => {
    if (open && leadId) load();
    // eslint-disable-next-line
  }, [open, leadId]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch(`/leads/${leadId}`, patch);
      setLead(r.data);
      onChanged?.();
      toast.success("Lead updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const addComm = async () => {
    if (!commForm.body.trim()) return;
    try {
      await api.post("/communications", {
        type: commForm.type,
        direction: "outbound",
        subject: commForm.subject || null,
        body: commForm.body,
        related_type: "lead",
        related_id: leadId,
      });
      setCommForm({ type: commForm.type, body: "", subject: "" });
      const c = await api.get(`/communications`, { params: { related_type: "lead", related_id: leadId }});
      setComms(c.data);
      toast.success("Logged to timeline");
    } catch (e) {
      toast.error("Failed to log");
    }
  };

  const removeLead = async () => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    await api.delete(`/leads/${leadId}`);
    toast.success("Lead deleted");
    onChanged?.();
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        data-testid="lead-drawer"
        className="w-full sm:max-w-[560px] p-0 bg-white overflow-y-auto"
      >
        <SheetTitle className="sr-only">Lead detail</SheetTitle>
        {lead && (
          <div>
            <div className="px-6 pt-6 pb-4 border-b border-[var(--miro-border)]">
              <div className="miro-overline">Lead detail</div>
              <SheetHeader className="mt-1">
                <SheetTitle className="font-display font-black text-2xl text-[var(--miro-navy)] tracking-tight">
                  {lead.name}
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Badge style={{ background: STATUS_COLOR[lead.status]?.bg, color: STATUS_COLOR[lead.status]?.fg }}
                       className="border-0 font-semibold">
                  {LEAD_STATUSES.find(s => s.value === lead.status)?.label}
                </Badge>
                <span className="text-xs text-[var(--miro-text-muted)]">·</span>
                <span className="text-xs font-medium text-[var(--miro-text-secondary)]">{divisionLabel(lead.division)}</span>
                <span className="text-xs text-[var(--miro-text-muted)]">·</span>
                <span className="text-xs text-[var(--miro-text-muted)]">Source: {lead.source}</span>
                {(lead.source_ref_code || lead.source_partner_code) && (
                  <span
                    data-testid="lead-referrer-badge"
                    className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded"
                    style={{ background: "rgba(95,191,58,0.15)", color: "#166534" }}
                    title="Referred by this partner / employee"
                  >
                    Ref: {lead.source_ref_code || lead.source_partner_code}
                  </span>
                )}
                <button
                  data-testid="lead-delete-btn"
                  onClick={removeLead}
                  className="ml-auto text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                >
                  <Trash2 size={13}/> Delete
                </button>
              </div>
            </div>

            <Tabs defaultValue="overview" className="px-6 pt-4">
              <TabsList data-testid="lead-tabs" className="bg-[var(--miro-bg)]">
                <TabsTrigger value="overview" data-testid="lead-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="lead-tab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="ai" data-testid="lead-tab-ai">AI Assist</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="pt-4 space-y-4 pb-8">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Email">{lead.email || "—"}</Field>
                  <Field label="Phone">{lead.phone || "—"}</Field>
                  <Field label="Est. value">₹{(lead.estimated_value || 0).toLocaleString("en-IN")}</Field>
                  <Field label="SLA due">{lead.sla_due_at ? new Date(lead.sla_due_at).toLocaleString() : "—"}</Field>
                </div>

                <div>
                  <Label>Status</Label>
                  <Select value={lead.status} onValueChange={(v) => save({ status: v })}>
                    <SelectTrigger data-testid="lead-status-select" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Division</Label>
                  <Select value={lead.division} onValueChange={(v) => save({ division: v })}>
                    <SelectTrigger data-testid="lead-division-select" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {DIVISIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assigned to</Label>
                  <Select value={lead.assigned_to || "unassigned"} onValueChange={(v) => save({ assigned_to: v === "unassigned" ? null : v })}>
                    <SelectTrigger data-testid="lead-assignee-select" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users
                        .filter(u => u.role === "super_admin" || u.role === "employee")
                        .map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role.replace(/_/g," ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Interest / notes</Label>
                  <Textarea
                    data-testid="lead-interest-textarea"
                    value={lead.interest || ""}
                    onChange={(e) => setLead({ ...lead, interest: e.target.value })}
                    onBlur={() => save({ interest: lead.interest })}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                {lead.division === "indian_admissions" && (
                  <div className="rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-3 space-y-3">
                    <div className="miro-overline">Indian Admission details</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Admission type</Label>
                        <Input
                          data-testid="lead-admission-type"
                          value={lead.admission_type || ""}
                          onChange={(e) => setLead({ ...lead, admission_type: e.target.value })}
                          onBlur={() => save({ admission_type: lead.admission_type || null })}
                          className="mt-1 bg-white"
                          placeholder="e.g. MBA, MBBS, BTech"
                        />
                      </div>
                      <div>
                        <Label>Program / Institution</Label>
                        <Input
                          data-testid="lead-admission-program"
                          value={lead.admission_program || ""}
                          onChange={(e) => setLead({ ...lead, admission_program: e.target.value })}
                          onBlur={() => save({ admission_program: lead.admission_program || null })}
                          className="mt-1 bg-white"
                          placeholder="e.g. Kalinga MBA – Marketing"
                        />
                      </div>
                      <div>
                        <Label>Mode</Label>
                        <Select
                          value={lead.admission_mode || "unset"}
                          onValueChange={(v) => save({ admission_mode: v === "unset" ? null : v })}
                        >
                          <SelectTrigger data-testid="lead-admission-mode" className="mt-1 bg-white"><SelectValue placeholder="Select…"/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unset">—</SelectItem>
                            {ADMISSION_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Duration</Label>
                        <Input
                          data-testid="lead-admission-duration"
                          value={lead.admission_duration || ""}
                          onChange={(e) => setLead({ ...lead, admission_duration: e.target.value })}
                          onBlur={() => save({ admission_duration: lead.admission_duration || null })}
                          className="mt-1 bg-white"
                          placeholder="e.g. 2 years"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Fee (₹)</Label>
                        <Input
                          data-testid="lead-admission-fee"
                          type="number"
                          min={0}
                          value={lead.admission_fee ?? ""}
                          onChange={(e) => setLead({ ...lead, admission_fee: e.target.value })}
                          onBlur={() => save({ admission_fee: lead.admission_fee === "" || lead.admission_fee == null ? null : Number(lead.admission_fee) })}
                          className="mt-1 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-[var(--miro-text-muted)]">
                  {saving ? "Saving…" : "Changes save automatically"}
                </div>
              </TabsContent>

              <TabsContent value="timeline" className="pt-4 pb-8">
                <div className="rounded-lg border border-[var(--miro-border)] p-3 bg-[var(--miro-bg)]">
                  <div className="flex items-center gap-2 mb-2">
                    <Select value={commForm.type} onValueChange={(v) => setCommForm({ ...commForm, type: v })}>
                      <SelectTrigger data-testid="comm-type-select" className="w-32 h-8 text-xs bg-white"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="note">Note</SelectItem>
                        <SelectItem value="call">Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                      </SelectContent>
                    </Select>
                    {commForm.type === "email" && (
                      <Input
                        data-testid="comm-subject-input"
                        placeholder="Subject"
                        className="h-8 text-xs bg-white"
                        value={commForm.subject}
                        onChange={(e) => setCommForm({ ...commForm, subject: e.target.value })}
                      />
                    )}
                  </div>
                  <Textarea
                    data-testid="comm-body-textarea"
                    placeholder="Log a call, note, message…"
                    value={commForm.body}
                    onChange={(e) => setCommForm({ ...commForm, body: e.target.value })}
                    rows={3}
                    className="bg-white"
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      data-testid="comm-add-btn"
                      size="sm"
                      onClick={addComm}
                      disabled={!commForm.body.trim()}
                      style={{ background: "var(--miro-navy)" }}
                    >
                      Log entry
                    </Button>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {comms.length === 0 && (
                    <div className="text-sm text-[var(--miro-text-muted)] text-center py-8">
                      No activity yet.
                    </div>
                  )}
                  {comms.map((c) => {
                    const Icon = COMM_ICONS[c.type] || StickyNote;
                    return (
                      <div key={c.id} data-testid={`comm-item-${c.id}`}
                           className="flex gap-3 rounded-lg border border-[var(--miro-border)] p-3 bg-white">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                             style={{ background: "#EFF6FF", color: "#0284C7" }}>
                          <Icon size={15}/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[11px] text-[var(--miro-text-muted)]">
                            <span className="uppercase tracking-wider font-semibold">{c.type}</span>
                            <span>·</span>
                            <span>{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</span>
                          </div>
                          {c.subject && <div className="font-semibold text-sm text-[var(--miro-navy)] mt-1">{c.subject}</div>}
                          <div className="text-sm text-[var(--miro-text-secondary)] whitespace-pre-wrap mt-1">
                            {c.body}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="ai" className="pt-4 pb-8">
                <AiAssistPanel
                  entityType="lead"
                  entityId={leadId}
                  onLogCommunication={() => api.get(`/communications`, { params: { related_type: "lead", related_id: leadId }}).then(r => setComms(r.data))}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div className="rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-3">
      <div className="miro-overline">{label}</div>
      <div className="text-sm font-medium text-[var(--miro-navy)] mt-1 truncate">{children}</div>
    </div>
  );
}
